/* ============================================================
   PRO STUDIO v3.9 — BLOK BINAAN KONGSI
   ============================================================
   Dipakai oleh ProStudioV39View dan ProStudioAgentStepperV39.
   Ia duduk dalam failnya sendiri semata-mata kerana DUA fail
   memerlukannya — kalau salah satu mengimport daripada yang satu
   lagi, kedua-duanya saling mengimport dan bulatan itu memecahkan
   build.

   ⛔ Peraturan yang sama seperti design/ui.jsx: JANGAN tulis kod
      warna di sini. Semua nilai daripada token --s-*, kalau tidak
      kawalan ini kekal warna gelap di tengah halaman putih apabila
      pengguna menukar tema.

   Kenapa tidak terus dalam design/ui.jsx: fail itu ialah sistem
   reka bentuk untuk SELURUH app. Blok di bawah ini bentuk khusus
   PRO Studio (label kecil di atas medan, pil pilihan, kad bertajuk
   dengan jalur kaki). Kalau ia terbukti berguna di tempat lain
   nanti, barulah ia naik ke design/ui.jsx.
   ============================================================ */

import { Check, Info } from 'lucide-react';

import '../design/tokens.css';

/* Label kecil di atas medan. `aksi` = kawalan kecil di hujung kanan
   baris label (contoh: suis "Rawak" pada seksyen Gaya). */
export const Lbl = ({ children, aksi }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, color: 'var(--s-ink-3)' }}>{children}</span>
        {aksi}
    </div>
);

export const Inp = ({ style, ...rest }) => (
    <input
        {...rest}
        style={{
            width: '100%', height: 36, borderRadius: 8, boxSizing: 'border-box',
            border: '1px solid var(--s-line-strong)', background: 'transparent',
            color: 'var(--s-ink)', padding: '0 11px', fontSize: 14,
            fontFamily: 'inherit', outline: 'none', ...style,
        }}
    />
);

export const Ta = ({ style, ...rest }) => (
    <textarea
        {...rest}
        style={{
            width: '100%', minHeight: 60, resize: 'vertical', boxSizing: 'border-box',
            border: '1px solid var(--s-line-strong)', borderRadius: 8, background: 'transparent',
            color: 'var(--s-ink)', padding: '9px 11px', fontSize: 13.5, lineHeight: 1.55,
            fontFamily: 'inherit', outline: 'none', ...style,
        }}
    />
);

/* Pemilih nilai. `auto` memudarkan teks supaya nilai lalai kelihatan
   sebagai keadaan lalai, bukan sebagai pilihan yang sudah dibuat. */
export const Sel = ({ auto, ...rest }) => (
    <select
        {...rest}
        style={{
            width: '100%', height: 36, borderRadius: 8, boxSizing: 'border-box',
            border: '1px solid var(--s-line-strong)', background: 'var(--s-fill)',
            color: auto ? 'var(--s-ink-3)' : 'var(--s-ink)',
            padding: '0 10px', fontSize: 13.5, fontFamily: 'inherit',
            outline: 'none', cursor: 'pointer',
        }}
    />
);

/* Pil boleh tekan. Keadaan "dipilih" memakai --s-select (putih pada
   tema gelap, hitam pada tema terang) — bukan warna jenama; jenama
   tidak digunakan pada kawalan. */
export const Cip = ({ on, kunci, kecil, ikon, children, ...rest }) => (
    <button
        type="button"
        disabled={kunci}
        {...rest}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            height: kecil ? 28 : 32, padding: kecil ? '0 10px' : '0 12px',
            borderRadius: 999, whiteSpace: 'nowrap',
            fontFamily: 'inherit', fontSize: kecil ? 12.5 : 13, fontWeight: 500, lineHeight: 1,
            cursor: kunci ? 'not-allowed' : 'pointer',
            background: on ? 'var(--s-select)' : 'transparent',
            color: on ? 'var(--s-select-ink)' : kunci ? 'var(--s-ink-3)' : 'var(--s-ink-2)',
            border: `1px solid ${on ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
            opacity: kunci ? 0.5 : 1,
            transition: 'background .12s ease, border-color .12s ease, color .12s ease',
        }}
    >
        {ikon}{children}
    </button>
);

/* jenis: info | ok | warn | stop */
export const Nota = ({ jenis = 'info', children }) => {
    const warna = jenis === 'warn' ? 'var(--s-warn)'
        : jenis === 'ok' ? 'var(--s-ok)'
            : jenis === 'stop' ? 'var(--s-stop)' : 'var(--s-ink-2)';
    const isian = jenis === 'warn' ? 'var(--s-warn-soft)'
        : jenis === 'ok' ? 'var(--s-ok-soft)'
            : jenis === 'stop' ? 'var(--s-stop-soft)' : 'var(--s-fill)';
    const Ikon = jenis === 'ok' ? Check : Info;
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px',
            borderRadius: 8, background: isian, color: warna, fontSize: 12.5, lineHeight: 1.55,
        }}>
            <Ikon size={13} strokeWidth={2} style={{ marginTop: 2, flex: 'none' }} />
            <span>{children}</span>
        </div>
    );
};

/* Kad bertajuk. Bentuk yang sama seperti kad Settings v3.9: kepala
   tanpa garis, jalur kaki bergaris untuk tindakan. */
export const Kad = ({ tajuk, perihal, kanan, kaki, children }) => (
    <div className="s-panelbox">
        {(tajuk || kanan) && (
            <div className="s-cardhead">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="s-cardhead__t">{tajuk}</h3>
                    {perihal && <p className="s-cardhead__d">{perihal}</p>}
                </div>
                {kanan}
            </div>
        )}
        {children && <div style={{ padding: 16 }}>{children}</div>}
        {kaki && <div className="s-cardfoot">{kaki}</div>}
    </div>
);
